/**
 * Offline banner + sync pill + the "chưa đồng bộ" list.
 *
 * THE PRODUCT RULE THESE THREE ENFORCE: the employee must be able to see, at
 * any moment, exactly which of their taps have reached the server and which
 * have not. An app that silently swallows a check-in is an app that costs
 * someone a day's pay.
 *
 * `@sonauto/ui` ships `OfflineBanner` and `SyncStatusPill`, but both are built
 * for the Admin/POS layouts (a top strip and a header chip with no drill-in).
 * The Staff App needs the pill to OPEN the queue — an employee who can see "2
 * chờ gửi" and cannot see WHICH two is no better off — so these wrap the shared
 * `SyncState` union and `syncStateLabel` from `@sonauto/offline-sync` in a
 * phone-shaped presentation instead.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { OutboxRecord } from '@sonauto/offline-sync';
import { useOutbox, useSyncState } from '../lib/hooks';
import { discardEntry, drainOutbox, retryEntry } from '../lib/outbox';
import { formatTimeDate } from '../lib/format';
import { syncStateLabel } from '../lib/sync-status';
import { Button, Sheet, StatusBadge } from './ui';

/** Fixed strip under the header. Only rendered when there is bad news. */
export function OfflineBanner(): JSX.Element | null {
  const { online, pending, failed, conflicts } = useSyncState();
  if (online && pending === 0 && failed === 0 && conflicts === 0) return null;

  if (!online) {
    return (
      <div role="status" className="bg-slate-800 px-4 py-2 text-center text-sm font-semibold text-white">
        Đang ngoại tuyến — chấm công và báo hoàn thành việc sẽ được lưu lại và gửi khi có mạng.
      </div>
    );
  }

  // A CONFLICT outranks everything: only a human can clear it.
  if (conflicts > 0) {
    return (
      <div role="alert" className="bg-rose-700 px-4 py-2 text-center text-sm font-semibold text-white">
        {conflicts} thao tác bị máy chủ từ chối. Mở “Chưa đồng bộ” để xử lý.
      </div>
    );
  }

  if (failed > 0) {
    return (
      <div role="alert" className="bg-amber-600 px-4 py-2 text-center text-sm font-semibold text-white">
        {failed} thao tác chưa gửi được, hệ thống sẽ tự thử lại.
      </div>
    );
  }

  return (
    <div role="status" className="bg-amber-500 px-4 py-2 text-center text-sm font-semibold text-white">
      Đang gửi {pending} thao tác đã lưu…
    </div>
  );
}

/**
 * The persistent pill. Never claims "đã đồng bộ" while anything is queued —
 * the label comes from `syncStateLabel`, which is derived from the queue itself
 * rather than from an optimistic local flag.
 */
export function SyncStatusPill(): JSX.Element {
  const { online, pending, failed, conflicts, state, lastSyncedAt } = useSyncState();
  const [open, setOpen] = useState(false);

  const outstanding = pending + failed + conflicts;
  const tone =
    !online
      ? 'bg-slate-200 text-slate-700'
      : conflicts > 0
        ? 'bg-rose-100 text-rose-800'
        : outstanding > 0
          ? 'bg-amber-100 text-amber-900'
          : 'bg-emerald-100 text-emerald-800';

  const label = outstanding > 0 && online ? `${syncStateLabel(state)} (${outstanding})` : syncStateLabel(state);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`tap rounded-full px-3 text-sm font-semibold ${tone}`}
        aria-label={`Trạng thái đồng bộ: ${label}`}
      >
        <span className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={`h-2 w-2 rounded-full ${online ? (outstanding > 0 ? 'bg-amber-500' : 'bg-emerald-500') : 'bg-slate-500'}`}
          />
          {label}
        </span>
      </button>
      <PendingSheet open={open} onClose={() => setOpen(false)} lastSyncedAt={lastSyncedAt} />
    </>
  );
}

function PendingSheet({
  open,
  onClose,
  lastSyncedAt,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly lastSyncedAt: number | null;
}): JSX.Element {
  const records = useOutbox();
  const { online } = useSyncState();

  return (
    <Sheet
      open={open}
      title="Thao tác chưa đồng bộ"
      onClose={onClose}
      footer={
        <Button size="lg" fullWidth disabled={!online} onClick={() => void drainOutbox()}>
          {online ? 'Gửi lại tất cả' : 'Cần có mạng để gửi'}
        </Button>
      }
    >
      <p className="mb-3 text-sm text-slate-600">
        Lần đồng bộ gần nhất: <strong>{formatTimeDate(lastSyncedAt)}</strong>
      </p>

      {records.length === 0 ? (
        <p className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-800">
          Mọi thao tác đã được gửi lên máy chủ.
        </p>
      ) : (
        <ul className="space-y-3">
          {records.map((record) => (
            <PendingRow key={record.id} record={record} />
          ))}
        </ul>
      )}

      <p className="mt-4 rounded-xl bg-slate-100 p-3 text-xs leading-relaxed text-slate-600">
        Lưu ý: <strong>giờ ghi nhận là giờ máy chủ nhận được</strong>, không phải giờ bạn bấm. Nếu bạn chấm
        công khi mất mạng, hãy báo quản lý để đối chiếu.
      </p>
    </Sheet>
  );
}

function PendingRow({ record }: { readonly record: OutboxRecord }): JSX.Element {
  const stuck = record.status === 'CONFLICT' || record.status === 'FAILED';
  const statusLabel =
    record.status === 'CONFLICT'
      ? 'Bị từ chối'
      : record.status === 'FAILED'
        ? 'Gửi lỗi'
        : record.status === 'SENDING'
          ? 'Đang gửi'
          : 'Chờ gửi';

  return (
    <li className="rounded-xl border border-slate-200 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-semibold text-slate-900">{record.label}</p>
          <p className="text-xs text-slate-500">
            Bạn bấm lúc {formatTimeDate(record.createdAt)}
            {record.attempts > 0 ? ` · đã thử ${record.attempts} lần` : ''}
          </p>
        </div>
        <StatusBadge tone={record.status === 'CONFLICT' ? 'danger' : record.status === 'FAILED' ? 'warning' : 'info'}>
          {statusLabel}
        </StatusBadge>
      </div>

      {record.lastError === null ? null : <p className="mt-2 text-sm text-rose-700">{record.lastError}</p>}

      {stuck ? (
        <div className="mt-3 flex gap-2">
          <Button variant="secondary" onClick={() => void retryEntry(record.id)}>
            Thử lại
          </Button>
          <Button variant="ghost" onClick={() => void discardEntry(record.id)}>
            Bỏ qua
          </Button>
        </div>
      ) : null}
    </li>
  );
}

/** Small inline notice used on screens whose actions are unavailable offline. */
export function OfflineDisabledNote({ what }: { readonly what: string }): JSX.Element | null {
  const { online } = useSyncState();
  if (online) return null;
  return (
    <p className="rounded-xl bg-slate-200 p-3 text-sm text-slate-700">
      Đang ngoại tuyến nên không thể {what}. Thao tác này phải gửi ngay lên máy chủ nên không lưu tạm được.{' '}
      <Link to="/ca-nhan" className="font-semibold underline">
        Xem chi tiết
      </Link>
    </p>
  );
}
